// src/hooks/useProductTranslation.ts

import { useCallback } from 'react';

export type Language = 'FR' | 'EN';

export type ProductTranslation = {
    language: Language;
    name: string;
    description: string;
};

/**
 * Returns the correct translation for a product.
 * Priority: preferredLanguage (org defaultLanguage) > EN > first available
 *
 * @param preferredLanguage - The org's defaultLanguage ('FR' | 'EN').
 *                            If not provided, falls back to 'EN'.
 */
export function useProductTranslation(preferredLanguage?: Language) {
    const lang: Language = preferredLanguage || 'EN';

    const getTranslation = useCallback((
        translations: ProductTranslation[] | undefined
    ): ProductTranslation | undefined => {
        if (!translations || translations.length === 0) return undefined;
        return (
            translations.find((t) => t.language === lang) ||
            translations.find((t) => t.language === 'EN') ||
            translations[0]
        );
    }, [lang]);

    const getName = useCallback((translations: ProductTranslation[] | undefined): string =>
        getTranslation(translations)?.name || '-',
        [getTranslation]);

    const getDescription = useCallback((translations: ProductTranslation[] | undefined): string =>
        getTranslation(translations)?.description || '-',
        [getTranslation]);

    return { getTranslation, getName, getDescription };
}
